import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { mediaQueryLarge } from '@theme/utilities';

const HOVER_CLOSE_DELAY = 200;
/** Keeps a pointer merely passing over the nav from popping the panel open. */
const HOVER_OPEN_DELAY = 100;

class HmMegaMenu extends Component {
  /** Whether the panel is currently open because of hover (desktop), not a click. */
  #openedByHover = false;

  #hoverCloseTimer = 0;
  #hoverOpenTimer = 0;

  /** Guards against Chrome scroll-anchoring drift while the overlay is open. */
  #scrollGuardActive = false;
  #lockedScrollY = 0;
  #lastScrollGestureAt = 0;

  /** @type {Map<string, any> | null} Drill-down panel data, see #getPanels. */
  #panels = null;

  /** Memoised #isTerminalPanel results. */
  #terminalPanels = new Map();

  /** @type {Map<string, string> | null} Memoised #getChainTargets result; blocks do not change after render. */
  #chainTargets = null;

  /** Ids of the currently open detail panels, outermost first — lets back navigate one level at a time. */
  #detailStack = [];

  connectedCallback() {
    super.connectedCallback();

    // Pagespeed (TBT): open() and #activateTab() sync the blocks right before a panel shows, so moving them
    // into the hidden panel during page load only adds style recalculation. The theme editor still needs
    // them in place immediately.
    if (window.Shopify?.designMode || this.hasAttribute('open')) this.#syncMenuBlocks();
    this.addEventListener('click', this.#handleClick);
    this.addEventListener('keyup', this.#handleKeyUp);
    mediaQueryLarge.addEventListener('change', this.#handleViewportChange);
    this.#handleViewportChange();
    this.#bindHoverTriggers();
    // capture phase: mouseenter doesn't bubble
    this.addEventListener('mouseenter', this.#handleInternalTabHoverEnter, true);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#handleBlockSelect);
      document.addEventListener('shopify:block:deselect', this.#handleBlockDeselect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#sourceObserver.disconnect();
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#handleBlockDeselect);
    if (this.#deselectTimer != null) clearTimeout(this.#deselectTimer);
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keyup', this.#handleKeyUp);
    mediaQueryLarge.removeEventListener('change', this.#handleViewportChange);
    this.#unbindHoverTriggers();
    this.removeEventListener('mouseenter', this.#handleInternalTabHoverEnter, true);
    document.removeEventListener('mousemove', this.#handleDocumentMouseMove);
    this.#cancelHoverClose();
    this.#cancelHoverOpen();
    this.#stopScrollGuard();
    if (this.hasAttribute('open')) {
      document.documentElement.removeAttribute('scroll-lock');
      document.documentElement.style.overflowAnchor = '';
      removeTrapFocus();
    }
  }

  #handleViewportChange = () => {
    if (!mediaQueryLarge.matches) this.close();
  };

  /**
   * @type {HTMLElement[]}
   */
  #hoverTriggers = [];

  #bindHoverTriggers() {
    this.#hoverTriggers = Array.from(document.querySelectorAll('[data-hm-hover-tab]'));
    for (const trigger of this.#hoverTriggers) {
      trigger.addEventListener('mouseenter', this.#handleTriggerMouseEnter);
      trigger.addEventListener('mouseleave', this.#handleTriggerMouseLeave);
      trigger.addEventListener('focus', this.#handleTriggerFocus);
    }
  }

  #unbindHoverTriggers() {
    for (const trigger of this.#hoverTriggers) {
      trigger.removeEventListener('mouseenter', this.#handleTriggerMouseEnter);
      trigger.removeEventListener('mouseleave', this.#handleTriggerMouseLeave);
      trigger.removeEventListener('focus', this.#handleTriggerFocus);
    }
    this.#hoverTriggers = [];
  }

  /**
   * @param {HTMLElement} trigger
   */
  #openTabFromHover(trigger) {
    if (!mediaQueryLarge.matches) return;

    const tabId = trigger.dataset.hmHoverTab;
    if (tabId == null) return;

    this.#cancelHoverClose();

    this.#activateTab(tabId);
    if (!this.hasAttribute('open')) this.open({ hover: true });
  }

  #handleTriggerMouseEnter = (event) => {
    const trigger = event.currentTarget;
    if (!(trigger instanceof HTMLElement)) return;

    this.#cancelHoverOpen();

    // Already open: switching tabs should feel instant, only the first open is delayed.
    if (this.hasAttribute('open')) {
      this.#openTabFromHover(trigger);
      return;
    }

    this.#hoverOpenTimer = window.setTimeout(() => {
      this.#hoverOpenTimer = 0;
      this.#openTabFromHover(trigger);
    }, HOVER_OPEN_DELAY);
  };

  #handleTriggerMouseLeave = () => {
    this.#cancelHoverOpen();
  };

  #cancelHoverOpen() {
    if (!this.#hoverOpenTimer) return;

    window.clearTimeout(this.#hoverOpenTimer);
    this.#hoverOpenTimer = 0;
  }

  #handleTriggerFocus = (event) => {
    if (!(event.currentTarget instanceof HTMLElement)) return;
    this.#openTabFromHover(event.currentTarget);
  };

  #handleInternalTabHoverEnter = (event) => {
    if (!this.#openedByHover || !mediaQueryLarge.matches) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const tabButton = target.closest('[data-hm-tab]');
    if (!(tabButton instanceof HTMLElement)) return;

    this.#activateTab(tabButton.dataset.hmTab ?? '0');
  };

  /**
   * Everything above the panel is the sticky header stack (announcement and
   * countdown bars, then the header itself), so that whole band counts as
   * safe — the panel opens flush under it and a pointer drifting up there is
   * still on the menu.
   *
   * The panel's safe width comes from `offsetWidth` (layout box), not
   * `getBoundingClientRect()` (paint box) — the panel slides in via a CSS
   * transform, so its paint rect keeps changing for ~500ms after opening
   * while its layout box is correct immediately.
   *
   * @param {number} clientX
   * @param {number} clientY
   */
  #isPointerInSafeArea(clientX, clientY) {
    /** @param {DOMRect} rect */
    const within = (rect) =>
      rect.width > 0 &&
      rect.height > 0 &&
      clientX >= rect.left &&
      clientX <= rect.right &&
      clientY >= rect.top &&
      clientY <= rect.bottom;

    for (const trigger of document.querySelectorAll('[data-hm-hover-tab]')) {
      if (within(trigger.getBoundingClientRect())) return true;
    }

    for (const section of document.querySelectorAll('.shopify-section-group-header-group')) {
      if (within(section.getBoundingClientRect())) return true;
    }

    const overlay = this.#overlay;
    const surface = this.#surface;
    if (!overlay || !surface) return false;

    const overlayRect = overlay.getBoundingClientRect();
    const safeLeft = overlayRect.left;
    const safeTop = overlayRect.top;

    if (clientY >= 0 && clientY <= safeTop) return true;

    return (
      clientX >= safeLeft &&
      clientX <= safeLeft + surface.offsetWidth &&
      clientY >= safeTop &&
      clientY <= safeTop + surface.offsetHeight
    );
  }

  /**
   * Closes the hover-opened panel once the pointer has left every safe area
   * for `HOVER_CLOSE_DELAY`. Driven by real `mousemove` samples rather than
   * `mouseenter`/`mouseleave` on the panel or its backdrop — those fire from
   * layout/visibility changes alone (the panel sliding in, the backdrop
   * newly becoming hit-testable) even while the pointer sits still, which
   * previously caused the menu to flicker open/closed.
   *
   * @param {MouseEvent} event
   */
  #handleDocumentMouseMove = (event) => {
    if (!this.#openedByHover) return;

    if (this.#isPointerInSafeArea(event.clientX, event.clientY)) {
      this.#cancelHoverClose();
      return;
    }

    this.#scheduleHoverClose();
  };

  #scheduleHoverClose() {
    if (this.#hoverCloseTimer) return;

    this.#hoverCloseTimer = window.setTimeout(() => {
      this.#hoverCloseTimer = 0;
      if (this.#openedByHover) this.close();
    }, HOVER_CLOSE_DELAY);
  }

  #cancelHoverClose() {
    if (!this.#hoverCloseTimer) return;

    window.clearTimeout(this.#hoverCloseTimer);
    this.#hoverCloseTimer = 0;
  }

  #markScrollGesture = () => {
    this.#lastScrollGestureAt = performance.now();
  };

  #handleWindowScroll = () => {
    if (!this.#scrollGuardActive) return;
    const hadRecentGesture = performance.now() - this.#lastScrollGestureAt < 150;
    if (!hadRecentGesture && window.scrollY !== this.#lockedScrollY) {
      window.scrollTo(0, this.#lockedScrollY);
    }
  };

  #startScrollGuard() {
    this.#lockedScrollY = window.scrollY;
    this.#lastScrollGestureAt = 0;
    this.#scrollGuardActive = true;
    window.addEventListener('wheel', this.#markScrollGesture, { passive: true });
    window.addEventListener('touchmove', this.#markScrollGesture, { passive: true });
    window.addEventListener('keydown', this.#markScrollGesture);
    window.addEventListener('scroll', this.#handleWindowScroll, { passive: true });
  }

  #stopScrollGuard() {
    this.#scrollGuardActive = false;
    window.removeEventListener('wheel', this.#markScrollGesture);
    window.removeEventListener('touchmove', this.#markScrollGesture);
    window.removeEventListener('keydown', this.#markScrollGesture);
    window.removeEventListener('scroll', this.#handleWindowScroll);
  }

  get #openButton() {
    return /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-hm-open]'));
  }

  get #overlay() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.hm-mega-menu__overlay'));
  }

  get #surface() {
    return /** @type {HTMLElement | null} */ (this.querySelector('.hm-mega-menu__surface'));
  }

  get #detailHost() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-hm-details]'));
  }

  #handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const openButton = target.closest('[data-hm-open]');
    if (openButton) {
      event.preventDefault();
      this.open();
      return;
    }

    const closeButton = target.closest('[data-hm-close]');
    if (closeButton) {
      event.preventDefault();
      this.close();
      return;
    }

    const tabButton = target.closest('[data-hm-tab]');
    if (tabButton instanceof HTMLElement) {
      // desktop: hover already previews tabs, so a click navigates like a normal link
      if (mediaQueryLarge.matches) return;

      event.preventDefault();
      this.#activateTab(tabButton.dataset.hmTab ?? '0');
      return;
    }

    const detailButton = target.closest('[data-hm-detail]');
    if (detailButton instanceof HTMLElement) {
      event.preventDefault();
      this.#openDetail(detailButton.dataset.hmDetail ?? '');
      return;
    }

    const backButton = target.closest('[data-hm-back]');
    if (backButton) {
      event.preventDefault();
      this.#closeActiveDetail();
    }
  };

  #handleKeyUp = (event) => {
    if (event.key === 'Escape') {
      this.close();
    }
  };

  /**
   * Theme editor: selecting a mega menu block in the sidebar must reveal it.
   * Every block lives in the hidden shared source until its tab is active, so
   * without this the merchant selects a block and sees nothing change.
   * @type {number | null}
   */
  #deselectTimer = null;

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    if (this.#deselectTimer != null) {
      clearTimeout(this.#deselectTimer);
      this.#deselectTimer = null;
    }

    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    const block = target.closest('[data-hm-block-type]');
    if (!(block instanceof HTMLElement) || !this.contains(block)) return;

    // The `open` attribute only lands on the next animation frame; the overlay
    // is the synchronous source of truth, so two quick selects cannot re-open
    // and close the detail panel this handler is about to show.
    if (this.#overlay?.hidden !== false) this.open();

    // Clicked in the preview: the event targets the hidden original, but one
    // of its clones is already on screen, so leave the panels alone instead of
    // closing and rebuilding the drill-down around it.
    const onScreenCopy = this.#onScreenCopyOf(block);
    if (onScreenCopy) {
      onScreenCopy.scrollIntoView({ block: 'nearest' });
      return;
    }

    const tabId = this.#tabIdForBlock(block);

    if (tabId != null) {
      this.#activateTab(tabId);
      block.scrollIntoView({ block: 'nearest' });
      return;
    }

    // A chain block matches no tab; show its own level 2 list as a drill-down.
    const chainTemplateId = block.dataset.hmChainTemplate;
    if (chainTemplateId) {
      this.#openDetail(chainTemplateId);
      return;
    }

    if (block.dataset.hmBlockType !== 'promo') return;

    const detailPanelId = this.#detailPanelIdForPromo(block);
    if (!detailPanelId) return;

    const ownerTabId = this.#tabIdForPanel(detailPanelId);
    if (ownerTabId != null) this.#activateTab(ownerTabId);

    this.#openDetail(detailPanelId);

    const promoClone = this.querySelector(
      `[data-hm-detail-panel="${CSS.escape(detailPanelId)}"] [data-hm-block-clone]`
    );
    if (promoClone instanceof HTMLElement) promoClone.scrollIntoView({ block: 'nearest' });
  };

  /**
   * The block itself or a clone of it that is visible in the current panel.
   * @param {HTMLElement} block
   * @returns {HTMLElement | null}
   */
  #onScreenCopyOf(block) {
    const editorId = block.getAttribute('data-shopify-editor-block');
    const activeDetail = this.querySelector('[data-hm-detail-panel][data-active]');
    const copies = editorId
      ? [...this.querySelectorAll('[data-hm-block-type][data-shopify-editor-block]')].filter(
          (copy) => copy.getAttribute('data-shopify-editor-block') === editorId
        )
      : [block];

    for (const copy of copies) {
      if (!(copy instanceof HTMLElement)) continue;
      if (activeDetail && !activeDetail.contains(copy)) continue;
      if (copy.checkVisibility?.() ?? copy.offsetParent !== null) return copy;
    }

    return null;
  }

  /** @param {Event} event */
  #handleBlockDeselect = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement) || !this.contains(target)) return;

    if (this.#deselectTimer != null) clearTimeout(this.#deselectTimer);
    // Switching between two of our blocks fires deselect then select; closing
    // on a timer lets the incoming select cancel it and avoids a flicker.
    this.#deselectTimer = setTimeout(() => {
      this.#deselectTimer = null;
      this.close();
    }, 120);
  };

  /**
   * @param {HTMLElement} block
   * @returns {string | null} data-hm-panel of the tab that owns this block
   */
  #tabIdForBlock(block) {
    const localeRoot = this.#localeRoot;
    const blockTargets = this.#getTargetCandidates(
      block.dataset.hmBlockType === 'promo'
        ? [block.dataset.hmPromoTarget ?? '', block.dataset.hmPromoTargetHandle ?? '']
        : [
            block.dataset.hmSubmenuTarget ?? '',
            block.dataset.hmSubmenuTargetHandle ?? '',
            block.dataset.hmSubmenuMenuHandle ?? '',
          ],
      localeRoot
    );

    for (const panel of this.querySelectorAll('[data-hm-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;

      const panelTargets = this.#getTargetCandidates(
        [panel.dataset.hmPanelTarget ?? '', panel.dataset.hmPanelUrl ?? '', panel.dataset.hmPanelHandle ?? ''],
        localeRoot
      );

      if (this.#targetSetsIntersect(panelTargets, blockTargets)) return panel.dataset.hmPanel ?? null;
    }

    return null;
  }

  /** @param {{ hover?: boolean }} [options] - hover opens without stealing focus */
  /**
   * Lines the panel's level 1 row up with the header nav it covers, so a tab
   * stays under the pointer that opened it. Measured rather than hard coded:
   * the offset depends on the logo, the locale and the store.
   */
  #syncTabOffset() {
    if (!mediaQueryLarge.matches) return;

    const trigger = document.querySelector('[data-hm-hover-tab]');
    const surface = this.#surface;
    if (!(trigger instanceof HTMLElement) || !surface) return;

    const offset = Math.round(trigger.getBoundingClientRect().left - surface.getBoundingClientRect().left);
    if (offset > 0) this.style.setProperty('--hm-menu-tab-offset', `${offset}px`);
  }

  /** @type {HTMLElement | null} */
  #hydrationHost = null;
  #hydrationKey = '';

  /**
   * Header hydration morphs this subtree against fresh server markup, stripping
   * `open` and rebuilding the panels under the shopper. The key only has to be
   * gone while the menu is on screen, so it is parked here and put back on close.
   */
  #suspendHydration() {
    if (this.#hydrationHost) return;

    const host = this.closest('[data-hydration-key]');
    if (!(host instanceof HTMLElement)) return;

    this.#hydrationHost = host;
    this.#hydrationKey = host.dataset.hydrationKey ?? '';
    delete host.dataset.hydrationKey;
  }

  #resumeHydration() {
    const host = this.#hydrationHost;
    if (!host) return;

    this.#hydrationHost = null;
    host.dataset.hydrationKey = this.#hydrationKey;
  }

  open({ hover = false } = {}) {
    this.#suspendHydration();
    this.#syncTabOffset();

    const overlay = this.#overlay;
    if (!overlay) return;

    this.#openedByHover = hover;
    this.#cancelHoverClose();
    // Hover never clicks outside the mini cart, so its own outside-click dismiss would leave it stacked over the menu.
    document.querySelector('cart-drawer-component')?.close?.();
    if (hover) {
      document.addEventListener('mousemove', this.#handleDocumentMouseMove);
    } else {
      document.removeEventListener('mousemove', this.#handleDocumentMouseMove);
    }
    this.#syncMenuBlocks();
    overlay.hidden = false;
    this.removeAttribute('closing');
    this.#openButton?.setAttribute('aria-expanded', 'true');
    document.documentElement.setAttribute('scroll-lock', '');
    document.documentElement.style.overflowAnchor = 'none';
    this.#startScrollGuard();
    this.#closeDetails();

    // Let the browser paint the off-canvas state before transitioning to open.
    requestAnimationFrame(() => {
      this.setAttribute('open', '');
      this.setAttribute('animating-open', '');

      window.setTimeout(() => {
        this.removeAttribute('animating-open');
      }, 760);

      if (!hover) {
        requestAnimationFrame(() => {
          trapFocus(overlay);
          const selectedTab = this.querySelector('[data-hm-tab][aria-selected="true"]');
          if (selectedTab instanceof HTMLElement) selectedTab.focus();
        });
      }
    });
  }

  close() {
    const overlay = this.#overlay;
    if (!overlay) return;

    this.#resumeHydration();
    document.removeEventListener('mousemove', this.#handleDocumentMouseMove);
    this.#cancelHoverClose();
    const wasOpenedByHover = this.#openedByHover;
    this.#openedByHover = false;

    this.removeAttribute('open');
    this.removeAttribute('animating-open');
    this.setAttribute('closing', '');
    this.#openButton?.setAttribute('aria-expanded', 'false');
    document.documentElement.removeAttribute('scroll-lock');
    document.documentElement.style.overflowAnchor = '';
    this.#stopScrollGuard();
    if (!wasOpenedByHover) removeTrapFocus();

    window.setTimeout(() => {
      if (!this.hasAttribute('open')) {
        overlay.hidden = true;
        this.removeAttribute('closing');
      }
    }, 520);

    if (!wasOpenedByHover) this.#openButton?.focus();
  }

  /**
   * @param {string} tabId
   */
  #activateTab(tabId) {
    for (const tab of this.querySelectorAll('[data-hm-tab]')) {
      tab.setAttribute('aria-selected', `${tab instanceof HTMLElement && tab.dataset.hmTab === tabId}`);
    }

    for (const panel of this.querySelectorAll('[data-hm-panel]')) {
      if (!(panel instanceof HTMLElement)) continue;
      panel.hidden = panel.dataset.hmPanel !== tabId;
    }

    this.#syncMenuBlocks();
    this.#closeDetails();
  }

  /** The panel whose submenu/promo blocks are currently moved in from the shared source. */
  #populatedPanel = null;

  /** How many block elements that panel ended up holding, clones included. */
  #populatedCount = 0;

  /**
   * Section hydration morphs this element against fresh server markup, which
   * puts every block back into the shared source and empties the panels. The
   * server only adds nodes to the source then, so watching it lets us move
   * the active tab's blocks back in. The markup is new, so everything derived
   * from it is dropped first.
   */
  #sourceObserver = new MutationObserver(() => {
    this.#dropMenuData();
    this.#syncMenuBlocks();
  });

  /**
   * Forgets everything read out of the rendered blocks: the menu data, the
   * chain map and the panels already built from them. The theme editor
   * re-renders the section on every settings change, so stale entries here
   * would keep serving the previous menu.
   */
  #dropMenuData() {
    this.#panels = null;
    this.#chainTargets = null;
    this.#terminalPanels.clear();

    for (const detailPanel of this.querySelectorAll('[data-hm-detail-panel]')) detailPanel.remove();
    this.#closeDetails();
  }

  get #blockSource() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-hm-blocks-source]'));
  }

  /**
   * Blocks are rendered once in `[data-hm-blocks-source]`. Only the active
   * tab's panel holds its matching blocks; they are moved (not cloned) so the
   * editor sees a single element per block. Promo cards also get a clone for
   * the mobile rail, stripped of the editor attribute.
   */
  #syncMenuBlocks() {
    const source = this.#blockSource;
    const panel = this.querySelector('[data-hm-panel]:not([hidden])');

    if (!source || !(panel instanceof HTMLElement)) return;

    // Skip only while the panel still holds every block we moved in; after a hydration morph they are gone.
    if (panel === this.#populatedPanel && panel.querySelectorAll('[data-hm-block-type]').length === this.#populatedCount) {
      // Re-attaching is a no-op when already observing, and covers a reconnected element.
      this.#sourceObserver.observe(source, { childList: true });
      return;
    }

    // Our own moves below must not retrigger the observer.
    this.#sourceObserver.disconnect();
    this.#releasePanelBlocks(source);
    this.#populatedPanel = panel;

    const localeRoot = this.#localeRoot;
    const panelTargets = this.#getTargetCandidates(
      [panel.dataset.hmPanelTarget ?? '', panel.dataset.hmPanelUrl ?? '', panel.dataset.hmPanelHandle ?? ''],
      localeRoot
    );
    const submenuContainer = panel.querySelector('.hm-mega-menu__submenus');
    const mobilePromos = panel.querySelector('.hm-mega-menu__mobile-promos');
    const desktopPromos = panel.querySelector('.hm-mega-menu__desktop-promos');
    const desktopPromoList = desktopPromos?.firstElementChild ?? desktopPromos;
    const activeSubmenuTargets = new Set();
    const promoBlocks = [];

    for (const block of source.querySelectorAll('[data-hm-block-type]')) {
      if (!(block instanceof HTMLElement)) continue;

      if (block.dataset.hmBlockType === 'submenu') {
        const submenuTargets = this.#getTargetCandidates(
          [
            block.dataset.hmSubmenuTarget ?? '',
            block.dataset.hmSubmenuTargetHandle ?? '',
            block.dataset.hmSubmenuMenuHandle ?? '',
          ],
          localeRoot
        );

        if (!this.#targetSetsIntersect(panelTargets, submenuTargets)) continue;

        for (const candidate of submenuTargets) activeSubmenuTargets.add(candidate);
        this.#activateBlock(block);
        submenuContainer?.append(block);
      } else if (block.dataset.hmBlockType === 'promo') {
        promoBlocks.push(block);
      }
    }

    let hasMatchingPromo = false;

    for (const block of promoBlocks) {
      const promoTargets = this.#getTargetCandidates(
        [block.dataset.hmPromoTarget ?? '', block.dataset.hmPromoTargetHandle ?? ''],
        localeRoot
      );

      if (
        !this.#targetSetsIntersect(panelTargets, promoTargets) &&
        !this.#targetSetsIntersect(activeSubmenuTargets, promoTargets)
      ) {
        continue;
      }

      hasMatchingPromo = true;
      this.#activateBlock(block);

      mobilePromos?.append(this.#clonePromoBlock(block));
      desktopPromoList?.append(block);
    }

    for (const promoContainer of [mobilePromos, desktopPromos]) {
      if (promoContainer instanceof HTMLElement) promoContainer.hidden = !hasMatchingPromo;
    }

    this.#populatedCount = panel.querySelectorAll('[data-hm-block-type]').length;
    this.#wireChainLeaves();
    this.#collapseTerminalNodes();
    this.#sourceObserver.observe(source, { childList: true });
  }

  /**
   * A promo card clone keeps its theme editor id so a merchant can click the
   * visible copy to select the block; outside the editor the id is dropped.
   * @param {HTMLElement} block
   * @returns {HTMLElement}
   */
  #clonePromoBlock(block) {
    const clone = /** @type {HTMLElement} */ (block.cloneNode(true));
    if (!window.Shopify?.designMode) clone.removeAttribute('data-shopify-editor-block');
    clone.setAttribute('data-hm-block-clone', '');
    return clone;
  }

  /** @param {HTMLElement} block */
  #activateBlock(block) {
    block.hidden = false;
    block.setAttribute('data-hm-block-active', '');
  }

  /**
   * Returns the previously populated panel's blocks to the shared source and
   * drops the mobile promo clones.
   * @param {HTMLElement} source
   */
  #releasePanelBlocks(source) {
    // Query every panel, not just the last populated one: a hydration morph can leave a stale reference.
    for (const block of this.querySelectorAll('[data-hm-panel] [data-hm-block-type]')) {
      if (!(block instanceof HTMLElement)) continue;

      if (block.hasAttribute('data-hm-block-clone')) {
        block.remove();
        continue;
      }

      block.removeAttribute('data-hm-block-active');
      source.append(block);
    }
  }

  /**
   * Converts any rendered leaf link whose title/URL matches another "Mega
   * menu submenu" block's target into a drill-down that opens that block's
   * own content — this is how a block can be chained underneath a deeper
   * item instead of only a level 1 tab, going past Shopify's native 3-level
   * menu limit. Safe to call repeatedly; already-wired leaves are skipped.
   */
  #wireChainLeaves() {
    const localeRoot = this.#localeRoot;
    const chainTargets = this.#getChainTargets(localeRoot);

    for (const leaf of this.querySelectorAll(
      '[data-hm-leaf-handle]:not([data-hm-detail]):not([data-hm-collapsed])'
    )) {
      if (!(leaf instanceof HTMLElement)) continue;

      const templateId = this.#chainTemplateIdFor(leaf, chainTargets, localeRoot);
      if (templateId) leaf.dataset.hmDetail = templateId;
    }
  }

  /**
   * The nav stops one level above the deepest category level: an item whose
   * children are all leaves keeps its own link and drops its drill-down, so
   * that last level can be shown as a row above the products instead. An
   * item that still has a child with children of its own keeps drilling, so
   * deeper trees (Kids) keep every level except their last.
   *
   * Only rendered items need the change; panels that are not built yet get
   * the same decision when they are created (see #createDetailItem).
   */
  #collapseTerminalNodes() {
    for (const item of this.querySelectorAll('[data-hm-detail]')) {
      if (!(item instanceof HTMLElement) || !this.#isTerminalPanel(item.dataset.hmDetail ?? '')) continue;

      delete item.dataset.hmDetail;
      // Marks an item turned into a plain link: it still has children in the menu, so its parent keeps drilling.
      item.dataset.hmCollapsed = '';
    }
  }

  /**
   * Menu data emitted by each submenu block, indexed once. Panels are built
   * from it on demand instead of being rendered for every level up front.
   * @returns {Map<string, { t: string, u?: string, p?: string, i: Array<{ t: string, u: string, h: string, d?: string }>, chain?: boolean }>}
   */
  #getPanels() {
    if (this.#panels) return this.#panels;

    this.#panels = new Map();
    const seenBlocks = new Set();

    for (const script of this.querySelectorAll('script[data-hm-menu-data]')) {
      const blockId = /** @type {HTMLElement} */ (script).dataset.hmMenuData ?? '';
      // The same block can be rendered more than once; its data is identical.
      if (!blockId || seenBlocks.has(blockId)) continue;
      seenBlocks.add(blockId);

      let data;
      try {
        data = JSON.parse(script.textContent ?? '{}');
      } catch {
        // One unreadable block must not cost every other block its drill-downs.
        continue;
      }

      for (const [panelId, panel] of Object.entries(data.panels ?? {})) this.#panels.set(panelId, panel);
      // The block's own level 2 list, chained under a deeper item of another block.
      this.#panels.set(blockId, { t: data.title, u: data.target ?? '', i: data.l2 ?? [], chain: true });
    }

    return this.#panels;
  }

  /**
   * True when every child of the panel is a leaf, i.e. the item opening it
   * collapses into a plain link. A child counts as having children when it
   * has its own panel or is chained to another block.
   * @param {string} panelId
   */
  #isTerminalPanel(panelId) {
    const cached = this.#terminalPanels.get(panelId);
    if (cached != null) return cached;

    const panel = this.#getPanels().get(panelId);
    const isTerminal =
      Boolean(panel?.i.length) &&
      !panel?.i.some((item) => item.d || this.#chainIdForUrl(item.u, item.h, panel.chain ? item.u : undefined));
    this.#terminalPanels.set(panelId, isTerminal);

    return isTerminal;
  }

  /**
   * @param {string} url
   * @param {string} handle
   * @param {string} [rawTarget] - level 2 items use the raw path, deeper ones the locale-less one
   * @returns {string | null}
   */
  #chainIdForUrl(url, handle, rawTarget) {
    const localeRoot = this.#localeRoot;
    const target = rawTarget != null ? (rawTarget.split('?')[0]?.split('#')[0] ?? '') : this.#toLiquidTarget(url);
    const chainTargets = this.#getChainTargets(localeRoot);

    for (const candidate of this.#getTargetCandidates([target, url, handle], localeRoot)) {
      if (!this.#isChainableCandidate(candidate)) continue;

      const templateId = chainTargets.get(candidate);
      if (templateId) return templateId;
    }

    return null;
  }

  /**
   * Same normalisation the Liquid `data-hm-leaf-target` used for drill-down
   * panel items: no query/hash, no locale root, no leading slash.
   * @param {string} url
   */
  #toLiquidTarget(url) {
    const root = (this.dataset.hmRootUrl ?? '').split('?')[0]?.split('#')[0] ?? '';
    const lastSlash = root.lastIndexOf('/');
    const localizedRoot = lastSlash < 0 ? root : root.slice(0, lastSlash) + root.slice(lastSlash + 1);
    let target = url.split('?')[0]?.split('#')[0] ?? '';

    if (!target) {
      target = '/';
    } else if (localizedRoot && localizedRoot !== '/' && target === localizedRoot) {
      target = '/';
    } else if (root && root !== '/' && target === root) {
      target = '/';
    } else if (root && root !== '/') {
      target = target.replace(root, '');
    }

    return target !== '/' && target.startsWith('/') ? target.slice(1) : target;
  }

  /**
   * @param {string} localeRoot
   * @returns {Map<string, string>} chain anchor path -> detail template id
   */
  #getChainTargets(localeRoot) {
    if (this.#chainTargets) return this.#chainTargets;

    /** @type {Map<string, string>} */
    const chainTargets = new Map();

    for (const chainBlock of this.querySelectorAll('[data-hm-chain-template]')) {
      if (!(chainBlock instanceof HTMLElement)) continue;
      const templateId = chainBlock.dataset.hmChainTemplate;
      if (!templateId) continue;

      const candidates = this.#getTargetCandidates(
        [
          chainBlock.dataset.hmSubmenuTarget ?? '',
          chainBlock.dataset.hmSubmenuTargetHandle ?? '',
          chainBlock.dataset.hmSubmenuMenuHandle ?? '',
        ],
        localeRoot
      );

      for (const candidate of candidates) {
        if (this.#isChainableCandidate(candidate)) chainTargets.set(candidate, templateId);
      }
    }

    this.#chainTargets = chainTargets;

    return chainTargets;
  }

  /**
   * @param {HTMLElement} leaf
   * @param {Map<string, string>} chainTargets
   * @param {string} localeRoot
   * @returns {string | null}
   */
  #chainTemplateIdFor(leaf, chainTargets, localeRoot) {
    // Level 2 leaves carry only the handle; their url and raw target come from
    // href, which is what the Liquid used to inline as data-hm-leaf-url/-target.
    const url = leaf.dataset.hmLeafUrl ?? leaf.getAttribute('href') ?? '';
    const target = leaf.dataset.hmLeafTarget ?? (url.split('?')[0]?.split('#')[0] ?? '');
    const candidates = this.#getTargetCandidates(
      [target, url, leaf.dataset.hmLeafHandle ?? ''],
      localeRoot
    );

    for (const candidate of candidates) {
      if (!this.#isChainableCandidate(candidate)) continue;

      const templateId = chainTargets.get(candidate);
      if (templateId) return templateId;
    }

    return null;
  }

  /**
   * "/" (home page) and "" (no link set yet) are not unique anchors — many
   * unrelated blocks and not-yet-filled-in leaves resolve to them, so they
   * must never be used to wire a chain link.
   * @param {string} candidate
   */
  #isChainableCandidate(candidate) {
    return candidate !== '' && candidate !== '/';
  }

  get #localeRoot() {
    const root =
      this.dataset.hmRootUrl ||
      document.documentElement.dataset.root ||
      document.documentElement.getAttribute('data-root') ||
      '';

    const localeRoot = this.#normalizePath(root).split('/')[0] ?? '';
    if (localeRoot) return localeRoot;

    const pathLocale = window.location.pathname.replace(/^\/+/, '').split('/')[0] ?? '';

    return this.#isLocaleSegment(pathLocale) ? pathLocale : '';
  }

  /**
   * @param {string[]} values
   * @param {string} localeRoot
   */
  #getTargetCandidates(values, localeRoot) {
    const candidates = new Set();

    for (const value of values) {
      this.#addTargetCandidate(candidates, this.#normalizePath(value));
      this.#addTargetCandidate(candidates, this.#normalizePath(value, localeRoot));

      for (const localeAgnosticValue of this.#getLocaleAgnosticPaths(value, localeRoot)) {
        this.#addTargetCandidate(candidates, localeAgnosticValue);
      }
    }

    return candidates;
  }

  /**
   * @param {Set<string>} firstSet
   * @param {Set<string>} secondSet
   */
  #targetSetsIntersect(firstSet, secondSet) {
    for (const candidate of secondSet) {
      if (firstSet.has(candidate)) return true;
    }

    return false;
  }

  /**
   * @param {Set<string>} candidates
   * @param {string} value
   */
  #addTargetCandidate(candidates, value) {
    if (!value) return;

    candidates.add(value);
    const normalizedHandle = this.#handleize(value);
    if (normalizedHandle) candidates.add(normalizedHandle);

    const segments = value.split('/').filter(Boolean);
    const lastSegment = segments.at(-1);
    if (lastSegment) {
      candidates.add(lastSegment);
      const lastSegmentHandle = this.#handleize(lastSegment);
      if (lastSegmentHandle) candidates.add(lastSegmentHandle);
    }
  }

  /**
   * @param {string} value
   * @param {string} localeRoot
   */
  #getLocaleAgnosticPaths(value, localeRoot) {
    const normalizedValue = this.#normalizePath(value);
    const paths = new Set([normalizedValue]);
    const normalizedLocaleRoot = this.#normalizePath(localeRoot);
    const segments = normalizedValue.split('/').filter(Boolean);
    const firstSegment = segments[0] ?? '';

    if (normalizedLocaleRoot && normalizedLocaleRoot !== '/') {
      paths.add(this.#normalizePath(normalizedValue, normalizedLocaleRoot));
    }

    if (this.#isLocaleSegment(firstSegment) && segments.length > 1) {
      paths.add(segments.slice(1).join('/'));
    }

    return paths;
  }

  /**
   * @param {string} value
   * @param {string} [localeRoot]
   */
  #normalizePath(value, localeRoot = '') {
    let normalizedValue = value.trim();
    if (!normalizedValue) return '';

    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(normalizedValue)) {
      try {
        normalizedValue = new URL(normalizedValue).pathname;
      } catch (_error) {
        normalizedValue = normalizedValue.split('?')[0]?.split('#')[0] ?? '';
      }
    }

    normalizedValue = normalizedValue.split('?')[0]?.split('#')[0] ?? '';

    if (normalizedValue !== '/') {
      normalizedValue = normalizedValue.replace(/^\/+/, '').replace(/\/+$/, '');
    }

    if (localeRoot && normalizedValue === localeRoot) {
      return '/';
    }

    if (localeRoot && normalizedValue.startsWith(`${localeRoot}/`)) {
      normalizedValue = normalizedValue.slice(localeRoot.length + 1);
    }

    return normalizedValue || '/';
  }

  /**
   * @param {string} value
   */
  #handleize(value) {
    return value
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  /**
   * @param {string} value
   */
  #isLocaleSegment(value) {
    return /^[a-z]{2}(?:-[a-z]{2})?$/i.test(value);
  }

  /**
   * @param {string} detailId
   */
  #openDetail(detailId) {
    this.removeAttribute('animating-open');
    this.setAttribute('detail-open', '');
    this.#freezeParentPanel();
    this.#ensureDetailPanel(detailId);
    this.#refreshDetailPromos(detailId);
    this.#wireChainLeaves();
    this.#collapseTerminalNodes();

    if (this.#detailStack.at(-1) !== detailId) this.#detailStack.push(detailId);

    for (const detailPanel of this.querySelectorAll('[data-hm-detail-panel]')) {
      if (!(detailPanel instanceof HTMLElement)) continue;
      detailPanel.hidden = detailPanel.dataset.hmDetailPanel !== detailId;
      detailPanel.toggleAttribute('data-active', detailPanel.dataset.hmDetailPanel === detailId);
      detailPanel.removeAttribute('data-closing');
    }

    const activePanel = this.querySelector(`[data-hm-detail-panel="${CSS.escape(detailId)}"]`);
    if (activePanel instanceof HTMLElement) {
      requestAnimationFrame(() => {
        const backButton = activePanel.querySelector('[data-hm-back]');
        if (backButton instanceof HTMLElement) backButton.focus();
      });
    }
  }

  /**
   * Blocks already moved in are no longer in the shared source, so this only
   * ever adds cards the panel has not seen yet.
   * @param {string} detailId
   */
  #refreshDetailPromos(detailId) {
    const detailPanel = this.querySelector(`[data-hm-detail-panel="${CSS.escape(detailId)}"]`);
    const panel = this.#getPanels().get(detailId);

    if (detailPanel instanceof HTMLElement && panel) this.#fillDetailPromos(detailPanel, panel);
  }

  /**
   * @param {string} detailId
   */
  #ensureDetailPanel(detailId) {
    if (this.querySelector(`[data-hm-detail-panel="${CSS.escape(detailId)}"]`)) return;

    const detailPanel = this.#createDetailPanel(detailId);
    if (detailPanel) this.#detailHost?.append(detailPanel);
  }

  /**
   * Builds one drill-down panel from the menu data and the shared shell.
   * @param {string} detailId
   * @returns {HTMLElement | null}
   */
  #createDetailPanel(detailId) {
    const shell = this.querySelector('template[data-hm-detail-shell]');
    const panel = this.#getPanels().get(detailId);
    if (!(shell instanceof HTMLTemplateElement) || !panel) return null;

    const detailPanel = shell.content.firstElementChild?.cloneNode(true);
    if (!(detailPanel instanceof HTMLElement)) return null;

    const { viewAll = '', back = '', backTo = '' } = shell.dataset;
    const backLabel = panel.chain ? back : backTo.replace('__HM_TITLE__', panel.p ?? '');

    detailPanel.setAttribute('data-hm-detail-panel', detailId);
    // The root itself is a label slot, which querySelectorAll does not include.
    for (const element of [detailPanel, ...detailPanel.querySelectorAll('[data-hm-slot="label"]')]) {
      element.setAttribute('aria-label', panel.t);
    }
    for (const element of detailPanel.querySelectorAll('[data-hm-slot="title"]')) element.textContent = panel.t;
    for (const element of detailPanel.querySelectorAll('[data-hm-slot="back"]')) element.setAttribute('aria-label', backLabel);

    const list = detailPanel.querySelector('[data-hm-slot="list"]');
    if (panel.chain) {
      // Reuse the block's own level 2 markup so the chained panel looks identical.
      const source = this.querySelector(`[data-hm-chain-template="${CSS.escape(detailId)}"] nav > ul`);
      for (const item of source?.children ?? []) list?.append(item.cloneNode(true));
    } else {
      for (const item of panel.i) list?.append(this.#createDetailItem(item, viewAll));
    }

    this.#fillDetailPromos(detailPanel, panel);

    return detailPanel;
  }

  /**
   * Fills in every promo card bound to this drill-down level: a card whose
   * target matches the url of the item that opened the panel belongs to that
   * panel only. Matching is by url, never by title — sibling branches reuse
   * titles (eight Kids branches all end in "Clothing").
   *
   * Unlike a level 1 tab, a detail panel is disposable — a hydration morph
   * can drop it — so the cards are cloned and the originals stay in the
   * shared source. Clones are rebuilt on every call, which keeps the panel
   * in sync after a theme editor edit.
   * @param {HTMLElement} detailPanel
   * @param {{ u?: string }} panel
   */
  #fillDetailPromos(detailPanel, panel) {
    const source = this.#blockSource;
    const mobilePromos = detailPanel.querySelector('.hm-mega-menu__mobile-promos');
    const desktopPromos = detailPanel.querySelector('.hm-mega-menu__desktop-promos');
    const desktopPromoList = desktopPromos?.firstElementChild ?? desktopPromos;

    if (!source || (!mobilePromos && !desktopPromos)) return;

    for (const staleClone of detailPanel.querySelectorAll('[data-hm-block-clone]')) staleClone.remove();

    const localeRoot = this.#localeRoot;
    const panelTargets = this.#getTargetCandidates([panel.u ?? ''], localeRoot);
    let hasPromo = false;

    for (const block of source.querySelectorAll('[data-hm-block-type="promo"]')) {
      if (!(block instanceof HTMLElement) || panelTargets.size === 0) continue;

      const promoTargets = this.#getTargetCandidates(
        [block.dataset.hmPromoTarget ?? '', block.dataset.hmPromoTargetHandle ?? ''],
        localeRoot
      );

      if (!this.#targetSetsIntersect(panelTargets, promoTargets)) continue;

      hasPromo = true;

      for (const promoContainer of [desktopPromoList, mobilePromos]) {
        if (!(promoContainer instanceof HTMLElement)) continue;

        const clone = this.#clonePromoBlock(block);
        clone.setAttribute('data-hm-block-active', '');
        clone.hidden = false;
        promoContainer.append(clone);
      }
    }

    for (const promoContainer of [mobilePromos, desktopPromos]) {
      if (promoContainer instanceof HTMLElement) promoContainer.hidden = !hasPromo;
    }
  }

  /**
   * The drill-down panel a promo card is bound to, if any.
   * @param {HTMLElement} block
   * @returns {string | null}
   */
  #detailPanelIdForPromo(block) {
    const localeRoot = this.#localeRoot;
    const promoTargets = this.#getTargetCandidates(
      [block.dataset.hmPromoTarget ?? '', block.dataset.hmPromoTargetHandle ?? ''],
      localeRoot
    );

    for (const [panelId, panel] of this.#getPanels()) {
      if (!panel.u) continue;
      if (this.#targetSetsIntersect(this.#getTargetCandidates([panel.u], localeRoot), promoTargets)) return panelId;
    }

    return null;
  }

  /**
   * @param {string} panelId
   * @returns {string | null} data-hm-panel of the tab whose block owns this panel
   */
  #tabIdForPanel(panelId) {
    for (const block of this.querySelectorAll('[data-hm-chain-template]')) {
      if (!(block instanceof HTMLElement)) continue;

      const templateId = block.dataset.hmChainTemplate ?? '';
      if (!templateId || (panelId !== templateId && !panelId.startsWith(`${templateId}-`))) continue;

      return this.#tabIdForBlock(block);
    }

    return null;
  }

  /**
   * @param {{ t: string, u: string, h: string, d?: string }} item
   * @param {string} viewAll - lowercased "View all" label
   */
  #createDetailItem(item, viewAll) {
    const listItem = document.createElement('li');
    if (item.t.toLowerCase().trim() === viewAll) listItem.className = 'hm-mega-menu__view-all-item';

    const link = document.createElement('a');
    link.href = item.u;
    link.textContent = item.t;
    link.dataset.hmLeafTarget = this.#toLiquidTarget(item.u);
    link.dataset.hmLeafUrl = item.u;
    link.dataset.hmLeafHandle = item.h;

    // An item with its own panel, or one chained to another block, drills down unless every child is a leaf.
    const panelId = item.d || this.#chainIdForUrl(item.u, item.h);
    link.className = item.d
      ? 'hm-mega-menu__main-link hm-mega-menu__detail-link tw:block tw:w-full tw:text-left tw:uppercase tw:no-underline'
      : 'hm-mega-menu__detail-link tw:uppercase tw:text-ink tw:no-underline';

    if (panelId && this.#isTerminalPanel(panelId)) {
      link.dataset.hmCollapsed = '';
    } else if (panelId) {
      link.dataset.hmDetail = panelId;
    }

    listItem.append(link);

    return listItem;
  }

  #closeDetails() {
    this.removeAttribute('detail-open');
    this.#unfreezeParentPanel();
    this.#detailStack = [];

    for (const detailPanel of this.querySelectorAll('[data-hm-detail-panel]')) {
      if (detailPanel instanceof HTMLElement) {
        detailPanel.hidden = true;
        detailPanel.removeAttribute('data-active');
        detailPanel.removeAttribute('data-closing');
      }
    }
  }

  /** Back button: closes the current detail panel and reveals the previous one on the stack, if any. */
  #closeActiveDetail() {
    const activePanel = this.querySelector('[data-hm-detail-panel][data-active]');
    if (!(activePanel instanceof HTMLElement)) return;

    this.#detailStack.pop();
    const previousId = this.#detailStack.at(-1);

    activePanel.removeAttribute('data-active');
    activePanel.setAttribute('data-closing', '');

    window.setTimeout(() => {
      if (activePanel.hasAttribute('data-active')) return;

      activePanel.hidden = true;
      activePanel.removeAttribute('data-closing');

      const previousPanel =
        previousId != null ? this.querySelector(`[data-hm-detail-panel="${CSS.escape(previousId)}"]`) : null;

      if (previousPanel instanceof HTMLElement) {
        previousPanel.hidden = false;
        previousPanel.setAttribute('data-active', '');

        const backButton = previousPanel.querySelector('[data-hm-back]');
        if (backButton instanceof HTMLElement) backButton.focus();
      } else {
        this.removeAttribute('detail-open');
        this.#unfreezeParentPanel();
      }
    }, 360);
  }

  #freezeParentPanel() {
    const activePanel = this.querySelector('[data-hm-panel]:not([hidden])');
    if (!(activePanel instanceof HTMLElement)) return;

    for (const animation of activePanel.getAnimations({ subtree: true })) {
      animation.cancel();
    }

    const parentAnimatedElements = activePanel.querySelectorAll(
      '.hm-mega-menu__animated-item, .hm-mega-menu__mobile-promos, .hm-mega-menu__desktop-promos'
    );

    for (const element of parentAnimatedElements) {
      if (!(element instanceof HTMLElement)) continue;
      element.dataset.hmFrozen = 'true';
      element.style.animation = 'none';
      element.style.opacity = '1';
      element.style.transform = 'none';
    }
  }

  #unfreezeParentPanel() {
    for (const element of this.querySelectorAll('[data-hm-frozen="true"]')) {
      if (!(element instanceof HTMLElement)) continue;
      delete element.dataset.hmFrozen;
      element.style.animation = '';
      element.style.opacity = '';
      element.style.transform = '';
    }
  }
}

if (!customElements.get('hm-mega-menu')) {
  customElements.define('hm-mega-menu', HmMegaMenu);
}
